import { useState } from 'react';

import {
  ArrowRight,
  Bot,
  BrainCircuit,
  Building,
  Crosshair,
  Database,
  Headphones,
  MessageSquare,
  Mic,
  Shield,
  ShieldAlert,
  Sparkles,
  UserPlus,
  Users,
  Volume2,
  Zap,
} from 'lucide-react';
import { motion } from 'motion/react';
import PropTypes from 'prop-types';

import { Button } from '@src/components/ui/button';

const aiAgents = [
  {
    id: 'greeter',
    name: 'Greeter',
    icon: MessageSquare,
    tools: 4,
    color: 'from-blue-500 to-blue-600',
    description: 'Entry point for all interactions, routes to specialists',
  },
  {
    id: 'grc',
    name: 'GRC',
    icon: Shield,
    tools: 50,
    color: 'from-purple-500 to-purple-600',
    description: 'Risk management, compliance, policies & frameworks',
  },
  {
    id: 'iam',
    name: 'IAM',
    icon: Users,
    tools: 28,
    color: 'from-amber-500 to-amber-600',
    description: 'Identity lifecycle, access reviews & entitlements',
  },
  {
    id: 'cmdb',
    name: 'CMDB',
    icon: Database,
    tools: 37,
    color: 'from-blue-500 to-cyan-600',
    description: 'Asset discovery, inventory & dependency mapping',
  },
  {
    id: 'xdr',
    name: 'XDR',
    icon: ShieldAlert,
    tools: 66,
    color: 'from-red-500 to-red-600',
    description: 'Threat detection, incident response & MITRE ATT&CK',
  },
  {
    id: 'business',
    name: 'Business',
    icon: Building,
    tools: 34,
    color: 'from-indigo-500 to-indigo-600',
    description: 'BCP, disaster recovery & organizational ops',
  },
  {
    id: 'pentester',
    name: 'PenTester',
    icon: Crosshair,
    tools: 185,
    color: 'from-rose-500 to-rose-600',
    description: '127 security tools across 76+ containers, 18-phase workflow with 42 scan types: Nmap, Metasploit, Nuclei, WPScan, BloodHound & more',
  },
  {
    id: 'support',
    name: 'Support',
    icon: Headphones,
    tools: 29,
    color: 'from-green-500 to-green-600',
    description: 'Service requests, tasks & ticket management',
  },
  {
    id: 'onboarding',
    name: 'Onboarding',
    icon: UserPlus,
    tools: 12,
    color: 'from-teal-500 to-teal-600',
    description: 'Customer setup, invitations & configuration',
  },
];

const totalTools = aiAgents.reduce((sum, agent) => sum + agent.tools, 0);
// Marketing copy rounds down to the nearest ten ('440+'), so it stays true as agents gain tools.
const toolsLabel = `${Math.floor(totalTools / 10) * 10}+`;

const keyFeatures = [
  {
    icon: Volume2,
    title: 'Voice-First Interface',
    description: 'Natural language voice commands multilingual support',
  },
  {
    icon: BrainCircuit,
    title: 'Specialist AI Agents',
    description: '9 agents (1 triage + 8 domain experts) trained on security operations best practices',
  },
  {
    icon: Zap,
    title: '440+ AI-Powered Tools',
    description: 'Comprehensive toolset covering every aspect of security operations',
  },
  {
    icon: Sparkles,
    title: 'Real-Time Intelligence',
    description: 'Instant answers, automated workflows, and proactive recommendations',
  },
];

function AgentCard({ agent, index }) {
  const IconComponent = agent.icon;
  return (
    <motion.div
      className="group"
      viewport={{ once: true }}
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 hover:shadow-lg hover:border-indigo-500/50 transition-all duration-300 h-full">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 bg-gradient-to-br ${agent.color} rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
            <IconComponent className="w-5 h-5 text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-white text-sm">{agent.name}</h4>

              <span className="text-xs font-medium bg-slate-700 text-slate-300 px-2 py-0.5 rounded-full">
                {agent.tools}
                {' '}
                tools
              </span>
            </div>

            <p className="text-xs text-slate-400 line-clamp-2">{agent.description}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

AgentCard.propTypes = {
  agent: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

export function AIAgentsSection({ onNavigateToTop }) {
  // useState lazy initializers are exempt from react-hooks/purity (useMemo's callback isn't).
  const [backgroundParticles] = useState(() => Array.from({ length: 20 }, () => ({
    left: Math.random() * 100,
    top: Math.random() * 100,
    duration: 3 + Math.random() * 2,
    delay: Math.random() * 2,
  })));
  const [voiceBars] = useState(() => Array.from({ length: 12 }, () => ({
    scaleYPeak: 0.5 + Math.random() * 0.5,
  })));

  return (
    <section className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-purple-500 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500 rounded-full blur-3xl opacity-30" />
      </div>

      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {backgroundParticles.map((p, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/30 rounded-full"
            style={{
              left: `${p.left}%`,
              top: `${p.top}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: p.duration,
              repeat: Infinity,
              delay: p.delay,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          viewport={{ once: true }}
          className="text-center mb-16"
          transition={{ duration: 0.8 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-400 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-blue-500/30">
            <Bot className="w-4 h-4 mr-2" />
            AI-First Platform
          </div>

          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
            Meet Your AI Security Team
          </h2>

          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            {aiAgents.length}
            {' '}
            specialized AI voice agents with
            {' '}

            <span className="text-blue-400 font-semibold">
              {toolsLabel}
              {' '}
              security tools
            </span>

            {' '}
            working together
            to automate your security operations. Just speak naturally—BOSSS handles the rest.
          </p>

          {/* Voice visualization */}
          <motion.div
            initial={{ opacity: 0 }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1 }}
            className="flex items-center justify-center gap-1 mb-8 h-10"
          >
            <Mic className="w-6 h-6 text-blue-400 mr-3" />

            <div className="flex items-center gap-1 h-10">
              {voiceBars.map((bar, i) => (
                <motion.div
                  key={i}
                  style={{ height: 40 }}
                  className="w-1 bg-gradient-to-t from-blue-500 to-purple-400 rounded-full"
                  animate={{
                    scaleY: [0.2, bar.scaleYPeak, 0.2],
                  }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    delay: i * 0.1,
                    ease: 'easeInOut',
                  }}
                />
              ))}
            </div>

            <Volume2 className="w-6 h-6 text-purple-400 ml-3" />
          </motion.div>
        </motion.div>

        {/* Key Features Grid */}
        <motion.div
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {keyFeatures.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <IconComponent className="w-6 h-6 text-white" />
                </div>

                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* AI Agents Grid */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0 }}
          viewport={{ once: true }}
          whileInView={{ opacity: 1 }}
        >
          <h3 className="text-xl font-semibold text-white mb-6 text-center">
            9 Specialist AI Agents
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {aiAgents.map((agent, index) => (
              <AgentCard agent={agent} index={index} key={agent.id} />
            ))}
          </div>
        </motion.div>

        {/* Example Interactions */}
        <motion.div
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mb-16"
        >
          <h3 className="text-xl font-semibold text-white mb-6 text-center">
            Just Ask BOSSS
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { agent: 'GRC', query: '"What critical risks need attention this week?"' },
              { agent: 'XDR', query: '"Are there any active security incidents?"' },
              { agent: 'IAM', query: '"Who has admin access to production systems?"' },
              { agent: 'PenTester', query: '"Run a vulnerability scan on example.com"' },
              { agent: 'Business', query: '"Which contracts expire in the next 90 days?"' },
              { agent: 'CMDB', query: '"Show me all assets missing security agents"' },
            ].map((example, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                    {example.agent}
                    {' '}
                    Agent
                  </span>
                </div>

                <p className="text-slate-300 text-sm italic">{example.query}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center"
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-12 border border-blue-500/20">
            <Sparkles className="w-12 h-12 text-blue-400 mx-auto mb-4" />

            <h3 className="text-2xl font-bold text-white mb-4">
              Experience AI-Powered Security Operations
            </h3>

            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Schedule a demo to see how BOSSS AI agents can automate your security workflows,
              answer questions in real-time, and supercharge your team&apos;s productivity.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg transition-all hover:scale-105"
                onClick={() => onNavigateToTop('/demo')}
              >
                <Mic className="w-4 h-4 mr-2" />
                Try AI Voice Demo
              </Button>

              <Button
                className="border-2 border-white/30 text-white bg-transparent hover:bg-white/10 px-8 py-3 rounded-lg transition-all"
                onClick={() => onNavigateToTop('/contact')}
              >
                Learn More
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

AIAgentsSection.propTypes = {
  onNavigate: PropTypes.func,
  onNavigateToTop: PropTypes.func.isRequired,
};

export default AIAgentsSection;
